import { EMPTY, ROCK, SAND, WATER, LAVA, STEAM, OBSIDIAN, WOOD, FIRE, OIL, BLACK_POWDER, ACID, ICE, GRAVEL } from './materials';

/** Contact reactions between two adjacent materials, described as a sparse list and compiled to a flat table. */

export interface Reaction {
  readonly a: number;
  readonly b: number;
  /** What `a` becomes. */
  readonly outA: number;
  /** What `b` becomes. */
  readonly outB: number;
  /** Probability per tick and per contact, out of 65536 (65536 = always). */
  readonly chance: number;
  /**
   * Radius of an explosion the reaction sets off at the cell of `b`, 0 if none. The simulation only queues it
   * (World.blasts): damage and craters are the game's business.
   */
  readonly blast?: number;
  /** Percent chance that `a` is used up (becomes empty) when the reaction fires: acid, 30 %. */
  readonly consume?: number;
}

export const CHANCE_ONE = 65536;

/** Out of CHANCE_ONE: a percentage of the GDD as a chance per tick. */
const pct = (p: number) => Math.round((p * CHANCE_ONE) / 100);

/** GDD: black powder in contact with fire or lava blows up with a radius of 8. */
export const POWDER_BLAST = 8;

/**
 * Ice touching lava blows up (author's rule; the GDD sets the lava into obsidian and melts the ice into water): a
 * steam explosion, a little smaller than the powder's.
 */
export const STEAM_BLAST = 6;

/** Fire melts ice; the GDD says 8 % per tick, the author wants it faster. */
export const FIRE_MELTS_ICE = 40;

/** Fire catching on wood, per tick and per contact; the GDD says 2.5 %, the author wants it to run through the wood. */
export const FIRE_BURNS_WOOD = 8;

/** Steam touching ice, per tick and per contact: it hardly slides along a roof before it drips. */
export const STEAM_CONDENSES = 15;

/** GDD: acid has 30 % chances of being used up each time it eats a cell. */
export const ACID_CONSUME = 30;

/** Reactions of the GDD (section 12), all waves built. Obsidian resists acid (no reaction). */
export const REACTIONS: readonly Reaction[] = [
  // The lava sets into obsidian and the water boils into steam.
  { a: WATER, b: LAVA, outA: STEAM, outB: OBSIDIAN, chance: CHANCE_ONE },
  // Water puts the fire out.
  { a: WATER, b: FIRE, outA: STEAM, outB: EMPTY, chance: CHANCE_ONE },
  { a: LAVA, b: WOOD, outA: LAVA, outB: FIRE, chance: pct(8) },
  { a: LAVA, b: OIL, outA: LAVA, outB: FIRE, chance: pct(30) },
  { a: LAVA, b: BLACK_POWDER, outA: LAVA, outB: FIRE, chance: CHANCE_ONE, blast: POWDER_BLAST },
  // The wood burns, then disappears; oil makes a sheet of fire; powder blows up, cell after cell.
  { a: FIRE, b: WOOD, outA: FIRE, outB: FIRE, chance: pct(FIRE_BURNS_WOOD) },
  { a: FIRE, b: OIL, outA: FIRE, outB: FIRE, chance: pct(30) },
  { a: FIRE, b: BLACK_POWDER, outA: FIRE, outB: FIRE, chance: CHANCE_ONE, blast: POWDER_BLAST },
  // Ice in lava: the lava sets, the ice flashes into steam and blows up. Fire melts ice.
  { a: LAVA, b: ICE, outA: OBSIDIAN, outB: STEAM, chance: CHANCE_ONE, blast: STEAM_BLAST },
  { a: FIRE, b: ICE, outA: FIRE, outB: WATER, chance: pct(FIRE_MELTS_ICE) },
  // Steam condenses on ice and falls as water (author's rule: the ice roof of the Steam Dome rains it back).
  { a: STEAM, b: ICE, outA: WATER, outB: ICE, chance: pct(STEAM_CONDENSES) },
  // Acid eats the ground.
  { a: ACID, b: ROCK, outA: ACID, outB: EMPTY, chance: pct(3), consume: ACID_CONSUME },
  { a: ACID, b: SAND, outA: ACID, outB: EMPTY, chance: pct(6), consume: ACID_CONSUME },
  { a: ACID, b: GRAVEL, outA: ACID, outB: EMPTY, chance: pct(4), consume: ACID_CONSUME },
  { a: ACID, b: WOOD, outA: ACID, outB: EMPTY, chance: pct(8), consume: ACID_CONSUME },
];

export interface CompiledReactions {
  readonly count: number;
  /** table[(first << 8) | second] = entry index + 1, or 0 for no reaction. Both orders are compiled. */
  readonly table: Uint16Array;
  readonly outFirst: Uint8Array;
  readonly outSecond: Uint8Array;
  readonly chance: Int32Array;
  /** Explosion radius per entry, 0 for none; `blastAtFirst` = 1 if it happens at the first cell of the pair. */
  readonly blast: Uint8Array;
  readonly blastAtFirst: Uint8Array;
  /** Chance (out of CHANCE_ONE) that the first / second cell is used up when the entry fires. */
  readonly consumeFirst: Int32Array;
  readonly consumeSecond: Int32Array;
  /** reactive[id] = 1 if the material appears in any reaction: step() skips the others at once. */
  readonly reactive: Uint8Array;
}

export function compileReactions(reactions: readonly Reaction[], materialCount: number): CompiledReactions {
  const table = new Uint16Array(256 * 256);
  const size = reactions.length * 2;
  const outFirst = new Uint8Array(size);
  const outSecond = new Uint8Array(size);
  const chance = new Int32Array(size);
  const blast = new Uint8Array(size);
  const blastAtFirst = new Uint8Array(size);
  const consumeFirst = new Int32Array(size);
  const consumeSecond = new Int32Array(size);
  const reactive = new Uint8Array(256);
  let n = 0;

  const add = (first: number, second: number, o1: number, o2: number, c: number, b: number, atFirst: number, c1: number, c2: number) => {
    const key = (first << 8) | second;
    if (table[key] !== 0) throw new Error(`duplicate reaction between materials ${first} and ${second}`);
    outFirst[n] = o1;
    outSecond[n] = o2;
    chance[n] = c;
    blast[n] = b;
    blastAtFirst[n] = atFirst;
    consumeFirst[n] = c1;
    consumeSecond[n] = c2;
    n++;
    table[key] = n;
  };

  for (const r of reactions) {
    for (const id of [r.a, r.b, r.outA, r.outB]) {
      if (!Number.isInteger(id) || id < 0 || id >= materialCount) throw new Error(`reaction uses unknown material ${id}`);
    }
    if (!Number.isInteger(r.chance) || r.chance < 1 || r.chance > CHANCE_ONE) throw new Error(`reaction chance must be 1-${CHANCE_ONE}`);
    const b = r.blast ?? 0;
    if (!Number.isInteger(b) || b < 0 || b > 255) throw new Error('reaction blast must be 0-255');
    const use = r.consume ?? 0;
    if (use < 0 || use > 100) throw new Error('reaction consume must be 0-100');
    const cu = pct(use);
    add(r.a, r.b, r.outA, r.outB, r.chance, b, 0, cu, 0);
    reactive[r.a] = 1;
    reactive[r.b] = 1;
    if (r.a !== r.b) add(r.b, r.a, r.outB, r.outA, r.chance, b, 1, 0, cu);
  }
  return { count: n, table, outFirst, outSecond, chance, blast, blastAtFirst, consumeFirst, consumeSecond, reactive };
}
